/**
 * 🔧 Inspector Formatting Utilities
 *
 * Consolidated formatting functions for the Inspector module.
 * Provides consistent formatting across all Inspector components.
 */

import { formatInUserTZ, formatInFPTimezone } from './timezoneUtils'

// ============================================================================
// Currency Formatting
// ============================================================================

export interface CurrencyOptions {
  /** Currency code (default: 'AUD') */
  currency?: string
  /** Minimum fraction digits (default: 2) */
  minimumFractionDigits?: number
  /** Maximum fraction digits (default: 2) */
  maximumFractionDigits?: number
  /** Locale (default: 'en-AU') */
  locale?: string
}

/**
 * 💰 Format currency values consistently
 * 
 * @param amount - The amount to format
 * @param options - Currency formatting options
 * @returns Formatted currency string
 * 
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1234.56, { currency: 'USD', locale: 'en-US' }) // "$1,234.56"
 */
export const formatCurrency = (amount: number, options: CurrencyOptions = {}): string => {
  const {
    currency = 'AUD',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-AU'
  } = options

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount)
}

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * 📊 Format numbers with optional decimal places
 * 
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 * 
 * @example
 * formatNumber(1234.56) // "1234.56"
 * formatNumber(1234.56789, 3) // "1234.568"
 */
export const formatNumber = (value: number, decimals: number = 2): string => {
  return value.toFixed(decimals)
}

/**
 * 📈 Format numbers with 'k' notation for large values
 * 
 * @param value - The number to format
 * @returns Formatted string with 'k' notation for values over 999
 * 
 * @example
 * formatNumberWithUnits(1234) // "1.2k"
 * formatNumberWithUnits(999) // "999"
 * formatNumberWithUnits(1000000) // "1.0M"
 */
export const formatNumberWithUnits = (value: number): string => {
  if (value === 0) return '0'
  
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    const thousands = value / 1_000
    const formatted = (Math.round(thousands * 10) / 10).toString()
    return `${formatted}k`
  }
  
  return value.toString()
}

/**
 * 🎯 Format record counts with pluralization
 * 
 * @param count - Number of records
 * @param singular - Singular form of the noun (default: "record")
 * @param plural - Plural form of the noun (default: "records")
 * @returns Formatted string with proper pluralization
 * 
 * @example
 * formatRecordCount(1) // "1 record"
 * formatRecordCount(1234) // "1.2k records"
 * formatRecordCount(5, "sale", "sales") // "5 sales"
 */
export const formatRecordCount = (
  count: number, 
  singular: string = "record", 
  plural: string = "records"
): string => {
  const formattedCount = formatNumberWithUnits(count)
  const noun = count === 1 ? singular : plural
  return `${formattedCount} ${noun}`
}

// ============================================================================
// Percentage Formatting
// ============================================================================

/**
 * 📊 Format percentage values consistently
 * 
 * @param value - The percentage value (0-1 for decimal, 0-100 for whole)
 * @param decimals - Number of decimal places (default: 1)
 * @param isDecimal - Whether value is in decimal format (default: false)
 * @returns Formatted percentage string
 * 
 * @example
 * formatPercentage(85.5) // "85.5%"
 * formatPercentage(0.855, 1, true) // "85.5%"
 */
export const formatPercentage = (
  value: number, 
  decimals: number = 1, 
  isDecimal: boolean = false
): string => {
  const percentage = isDecimal ? value * 100 : value
  return `${percentage.toFixed(decimals)}%`
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a BUSINESS DATE (YYYY-MM-DD) without timezone conversion.
 *
 * Use for: sale_date, invoice_date -- dates representing a specific
 * business day in the store's timezone. The backend converts UTC to
 * the FP's timezone and sends YYYY-MM-DD.
 *
 * Do NOT use for system timestamps (ls_updated_at, last_sync_at,
 * created_at) -- those should display in the viewer's timezone.
 */
export const formatBusinessDate = (dateString?: string | null): string => {
  if (!dateString) return 'N/A'
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return formatDate(dateString)
  const [, year, month, day] = match
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

const _monthYearFmt = new Intl.DateTimeFormat('en-AU', {
  month: 'short',
  year: 'numeric',
})

export const formatMonthYear = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return _monthYearFmt.format(d)
}

export type DateFormat = 'short' | 'medium' | 'long' | 'datetime' | 'time'

/**
 * 📅 Format date values consistently
 *
 * @param date - The date to format (string or Date object)
 * @param format - The format type (default: 'short')
 * @param options - Additional options including timezone
 * @returns Formatted date string
 *
 * @example
 * formatDate('2024-01-15') // "15 Jan 2024"
 * formatDate('2024-01-15T10:30:00Z', 'datetime') // "15 Jan 2024, 10:30 AM AEST"
 * formatDate('2024-01-15T10:30:00Z', 'datetime', { fpTimezone: 'America/New_York' }) // "15 Jan 2024, 10:30 AM"
 */
export const formatDate = (
  date?: string | Date,
  format: DateFormat = 'short',
  options?: {
    fpTimezone?: string
    includeTimezone?: boolean
  }
): string => {
  if (!date) return 'N/A'

  const opts = {
    includeTimezone: format === 'datetime',
    ...options
  }

  // Map format types to date-fns format strings
  const formatMap: Record<DateFormat, string> = {
    'short': 'dd MMM yyyy',
    'medium': 'dd MMMM yyyy',
    'long': 'EEEE, dd MMMM yyyy',
    'datetime': 'dd MMM yyyy, hh:mm a',
    'time': 'hh:mm a'
  }

  const formatString = formatMap[format] || 'dd MMM yyyy'

  // If FP timezone is specified, use it (for transaction data)
  if (opts.fpTimezone) {
    return formatInFPTimezone(date, opts.fpTimezone, {
      format: formatString,
      includeTimezone: opts.includeTimezone,
      fallback: 'Invalid Date'
    })
  }

  // Otherwise use user timezone (for admin/audit data)
  return formatInUserTZ(date, {
    format: formatString,
    includeTimezone: opts.includeTimezone,
    fallback: 'Invalid Date'
  })
}

/**
 * Whole days between an ISO timestamp and "now".
 *
 * Returns null when the input is missing or unparseable. Used by the
 * "Days Open" exception column and the matching summary card.
 */
export const getDaysOpen = (dateString?: string | null): number | null => {
  if (!dateString) return null
  const parsed = new Date(dateString)
  if (Number.isNaN(parsed.getTime())) return null
  const diffMs = Date.now() - parsed.getTime()
  if (diffMs < 0) return 0
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * Numeric "days" string for the Days Open column.
 * Falls back to "-" when the source date is missing.
 */
export const formatDaysOpen = (dateString?: string | null): string => {
  const days = getDaysOpen(dateString)
  if (days === null) return '-'
  if (days === 0) return 'Today'
  if (days === 1) return '1 day'
  return `${days} days`
}

/**
 * Compact relative age for the exception drawer's detected-date line:
 * "just now" / "Nm ago" / "Nh ago" / "Nd ago". Always a single unit (no
 * weeks/months rollup) so rows in the oldest-first list compare directly at a
 * glance - "57d ago" vs "50d ago". The exact date/time rides the row's hover
 * tooltip. Falls back to "-" when the source date is missing/unparseable.
 */
export const formatDetectedAge = (dateString?: string | null): string => {
  if (!dateString) return '-'
  const parsed = new Date(dateString)
  if (Number.isNaN(parsed.getTime())) return '-'
  const diffMs = Date.now() - parsed.getTime()
  if (diffMs < 60_000) return 'just now'
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

/**
 * Colour band for an open-days value. Matches the syncFreshness scale
 * (green ≤3, amber 4-13, red ≥14). Returns an MUI palette token so
 * callers can pass straight to `sx={{ color: ... }}`.
 */
export const daysOpenColor = (days: number | null): string => {
  if (days === null) return 'text.disabled'
  if (days >= 14) return 'error.main'
  if (days >= 4) return 'warning.main'
  return 'success.main'
}

/**
 * 📅 Format datetime values with time (shorthand for datetime format)
 *
 * @param date - The date to format
 * @param fpTimezone - Optional franchise partner timezone
 * @returns Formatted datetime string
 */
export const formatDateTime = (date?: string | Date, fpTimezone?: string): string => {
  return formatDate(date, 'datetime', { fpTimezone, includeTimezone: true })
}

// ============================================================================
// Specialized Inspector Formatting
// ============================================================================

/**
 * 🔍 Format discrepancy amounts with high precision
 * Used for financial discrepancies and rounding differences
 * 
 * @param amount - The discrepancy amount
 * @param precision - Maximum decimal places (default: 8)
 * @returns Formatted discrepancy string without trailing zeros
 * 
 * @example
 * formatDiscrepancy(0.00000123) // "0.00000123"
 * formatDiscrepancy(1.20000000) // "1.2"
 */
export const formatDiscrepancy = (amount: number, precision: number = 8): string => {
  return amount.toFixed(precision).replace(/\.?0+$/, '')
}

/**
 * 👤 Format customer/client name for display
 * Combines first name, last name, and company consistently
 * 
 * @param customer - Customer data object
 * @returns Formatted client name string
 * 
 * @example
 * formatClientName({ customer_first_name: 'John', customer_last_name: 'Doe' }) // "John Doe"
 * formatClientName({ customer_company: 'Acme Corp' }) // "Acme Corp"
 * formatClientName({ customer_id: '12345' }) // "12345"
 */
export interface CustomerData {
  customer_id?: string
  customer_first_name?: string
  customer_last_name?: string
  customer_company?: string
}

export const formatClientName = (customer: CustomerData): string => {
  const parts = []
  
  if (customer.customer_first_name) parts.push(customer.customer_first_name)
  if (customer.customer_last_name) parts.push(customer.customer_last_name)
  if (customer.customer_company) parts.push(`(${customer.customer_company})`)
  
  return parts.length > 0 ? parts.join(' ') : customer.customer_id || 'N/A'
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * 🔢 Safe number parsing with fallback
 * 
 * @param value - Value to parse
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @returns Parsed number or fallback
 */
export const safeParseNumber = (value: any, fallback: number = 0): number => {
  if (typeof value === 'number' && !isNaN(value)) return value
  
  const parsed = parseFloat(value)
  return isNaN(parsed) ? fallback : parsed
}

/**
 * 🎨 Format status values with proper capitalization
 * 
 * @param status - Status string to format
 * @returns Properly formatted status string
 * 
 * @example
 * formatStatus('active') // "Active"
 * formatStatus('payment_pending') // "Payment Pending"
 */
export const formatStatus = (status: string): string => {
  if (!status) return 'Unknown'
  
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// ============================================================================
// Re-exports for backward compatibility
// ============================================================================

// Re-export commonly used functions with their original names
export { formatNumberWithUnits as formatNumberWithK }
export { formatDate as formatDateShort }
export { formatDateTime as formatDateTimeLong }

/**
 * 📚 Available formatting functions:
 * 
 * Currency:
 * - formatCurrency(amount, options?)
 * 
 * Numbers:
 * - formatNumber(value, decimals?)
 * - formatNumberWithUnits(value) - with K/M/B notation
 * - formatRecordCount(count, singular?, plural?)
 * - safeParseNumber(value, fallback?)
 * 
 * Percentages:
 * - formatPercentage(value, decimals?, isDecimal?)
 * 
 * Dates:
 * - formatDate(date?, format?)
 * - formatDateTime(date?)
 * 
 * Inspector-specific:
 * - formatDiscrepancy(amount, precision?)
 * - formatClientName(customer)
 * - formatStatus(status)
 */